// Trades log — the desk's deals over GET /api/trades, one block per traded
// asset, on the console's one ledger shape (components/Ledger.tsx).
//
// One fetched page (pageSize=200, the server cap); all filtering/sorting is
// client-side for v1. The filters sit in one bar at the top of the table
// card; desktop gets a full-height scroll of blocks beneath it, mobile
// horizontally scrollable tables. Row click opens DealSlideOver.
//
// FILTER BAR, NOT RAIL (operator, 18 Sept 2026, with a screenshot of this
// screen: "how can we make this left side console look better?"). The left
// rail was a 236px card holding Search and five chip groups wrapping over
// twelve ragged lines, beside a table that wanted the whole width. The rail is
// gone: the filters are one flex-wrap row inside the table card
// (components/FilterBar.tsx) — Search · When (period chips) · Type (four
// chips) · Asset (a select) · Status (a select) — with Clear filters at the
// row's end when anything is set, and the count line + Export CSV on their own
// line under it. The card takes the full width. Every state, the ?q / ?party
// doors, the sort, the currency blocks, the export and the deal panel are as
// they were; only the seat of the filters moved.
//
// Minimised 14 Sept 2026 (sweep §5.13 / item 53): "N fetched" is gone (two
// counts of two different things read as a contradiction), the Via column is
// gone (delivery is null on 150 of 248 deals — it stays in the deal panel),
// the column is "When", the group is "Type", the chip is "All", the period
// chips are the shared PeriodChips, and the absorbed mirror rows are hidden
// unless asked for — so the 1 Sept trades stop appearing twice.
//
// BOTH LEGS ON THE ROW (operator, 15 Sept 2026: "good but not the best"). One
// Volume cell showed the crypto leg alone, so "JP Office · Buy · 2,26,115 AED
// · 4.19" never said what JP bought. The row now reads as Book trade's own
// sentence, the customer's way round: Pays · Receives · Rate.
//
// CURRENCY-WISE BLOCKS (operator, 17 Sept 2026: "need better UI & Clear view,
// currency wise statements … same currency wise view on money/trades screens.
// still the screen looks mis-aligned … fonts too small, no colors properly
// used"). The one flat table is now one block per TRADED ASSET — the
// higher-ranked of a deal's two assets by the desk's pair rank (coins >
// stables > EUR > GBP > USD > AED > INR, lib/trade-math's orient), so a
// USDT·TRC/INR deal sits under USDT·TRC, EUR/AED under EUR, BTC/USDT under
// BTC. Blocks are ordered by their deal count; each carries its own header
// (bought · sold in that asset, the customer's side, and the count) and its
// own sticky column line. Sizes, widths and seats come from LEDGER, not from
// here — a fix there lands on every screen at once.

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Pending } from '../components/Pending';
import { EmptyState } from '../components/kit';
import { LAST_READ_NONE, ReadFailed } from '../components/OnboardingParts';
// The chip is shared with the money-movement ledger — one chip on the
// console, not one per log; the bar it sits in is shared the same way.
import { Chip } from '../components/FilterRail';
import { FilterBar, FilterItem, FilterPair, FilterSelect } from '../components/FilterBar';
import { PeriodChips, periodDays, periodWords, type PeriodValue } from '../components/PeriodChips';
import { Pill } from '../components/Pill';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, type TradeRow } from '../lib/api';
import { ScreenHeader } from '../components/ScreenHeader';
import { displayAsset, fmtAmount, fmtRate, fmtStamp, fmtTime, dubaiDate } from '../lib/format';
import { partyName } from '../lib/naming';
import { orient } from '../lib/trade-math';
import { LEDGER, LedgerHead, KindPill, Amount, DealRowLine, rateQuote } from '../components/Ledger';
import {
  DealSlideOver, type TradeKind,
  tradeKind, KIND_LABEL,
  lifecycleLabel, lifecycleTone, operatorLabel, viaWords,
} from '../components/DealSlideOver';

// ── filters ──────────────────────────────────────────────────────────────────

type KindFilter = 'all' | TradeKind;
type SortKey = 'time' | 'party' | 'rate';
type SortDir = 'asc' | 'desc';

const KINDS: Array<[KindFilter, string]> = [
  ['all', 'All'], ['buy', 'Buy'], ['sell', 'Sell'], ['mc', 'MC'],
];

/** The period chip → is this trade's Dubai day inside it? `periodDays` is the
 *  one place a chip becomes two desk days; `dubaiDate` the one place an
 *  instant becomes a day. */
function inPeriod(iso: string, days: { from?: string; to?: string }): boolean {
  const d = dubaiDate(iso);
  return (!days.from || d >= days.from) && (!days.to || d <= days.to);
}

/** THE STATE OF A ROW. A `legacy2:` deal id is the mirror's replay of a
 *  pre-cutover trade — it is carried into the 1 Sept opening box, whatever
 *  lifecycle the row says — so it reads "Carried in" and is folded with the
 *  absorbed rows (§5.13). No lifecycle in an older list response: non-MC deals
 *  are always written 'settled'; MC state is unknown from the row alone. */
export function rowLifecycle(t: TradeRow): string | null {
  if (t.deal_id.startsWith('legacy2:')) return 'absorbed';
  return t.lifecycle ?? (tradeKind(t) === 'mc' ? null : 'settled');
}

const FIAT = new Set(['AED', 'INR', 'USD', 'EUR', 'GBP']);

/** The deal's fiat leg (for toolbar volume totals); null when both legs crypto. */
function fiatLeg(t: TradeRow): { amount: string | number; asset: string } | null {
  if (FIAT.has(t.asset_take)) return { amount: t.amount_take, asset: t.asset_take };
  if (FIAT.has(t.asset_give)) return { amount: t.amount_give, asset: t.asset_give };
  return null;
}

/** THE TRADED ASSET of a deal: the higher-ranked of its two assets by the
 *  desk's pair rank — what `orient` calls the base. The block key is the
 *  displayed word, so legacy USDTTRC20 and canonical USDTTRC fold into one
 *  USDT·TRC block. */
function tradedAsset(t: TradeRow): string {
  return orient(t.asset_take, t.asset_give).base;
}

/** One block of the log: every deal whose traded asset is `label`, in the
 *  order the sort put them; bought/sold are the traded-asset leg summed the
 *  customer's way (bought = the customer receives it, sold = pays it). */
interface AssetBlock {
  label: string;
  /** A raw code of the traded asset, for fmtAmount's rounding rules. */
  code: string;
  deals: TradeRow[];
  bought: number;
  sold: number;
}

function groupByTraded(deals: TradeRow[]): AssetBlock[] {
  const blocks = new Map<string, AssetBlock>();
  for (const t of deals) {
    const code = tradedAsset(t);
    const label = displayAsset(code);
    let b = blocks.get(label);
    if (!b) { b = { label, code, deals: [], bought: 0, sold: 0 }; blocks.set(label, b); }
    b.deals.push(t);
    if (displayAsset(t.asset_give) === label) {
      const n = Number(t.amount_give);
      if (Number.isFinite(n)) b.bought += n;
    } else if (displayAsset(t.asset_take) === label) {
      const n = Number(t.amount_take);
      if (Number.isFinite(n)) b.sold += n;
    }
  }
  return [...blocks.values()].sort((a, b) => b.deals.length - a.deals.length || a.label.localeCompare(b.label));
}

// Party / merchant names on a trade row go through the same grammar as the
// rest of the console. The trades log itself NEVER filters by relevance — a
// trade is evidence, and a dormant party's deal is exactly the one you go
// looking for.
function tradeParty(t: TradeRow): string {
  return partyName({ display_name: t.party_name, slug: t.party_slug });
}
function tradeMerchant(t: TradeRow): string {
  if (!t.merchant_name && !t.merchant_slug) return '';
  return partyName({ display_name: t.merchant_name, slug: t.merchant_slug });
}

// ── CSV export (client-side blob over the filtered rows) ─────────────────────

function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** CSV carries the STORED party/merchant names, not the displayed ones. The
 *  export is a record, and a record has to match what the ledger and every
 *  other system hold; display normalization is a screen concern.
 *
 *  THE DAY COLUMN IS DUBAI'S. It used to write the raw ISO instant, so a trade
 *  booked 00:30 Dubai opened in a spreadsheet as 20:30 the day before — while
 *  the file it sat in was already named with dubaiDate(). `YYYY-MM-DD HH:MM`
 *  because a record has to sort and to parse as a date in a spreadsheet. The
 *  stored instant keeps its own trailing column, so nothing is lost.
 *
 *  The leg columns are the customer's (item 72): what the customer pays and
 *  what the customer receives — Book trade's words, not the ledger's. */
function exportCsv(rows: TradeRow[]): void {
  const header = [
    'Booked at (Dubai)', 'Deal id', 'Party', 'Type', 'Status',
    'Customer pays', 'Pays asset', 'Customer receives', 'Receives asset',
    'Rate', 'Spread', 'Via', 'Merchant', 'Booked by', 'Booked at (UTC)',
  ];
  const lines = rows.map((t) => [
    `${dubaiDate(t.booked_at)} ${fmtTime(t.booked_at)}`,
    t.deal_id, t.party_name, KIND_LABEL[tradeKind(t)], lifecycleLabel(rowLifecycle(t)),
    t.amount_take, displayAsset(t.asset_take),
    t.amount_give, displayAsset(t.asset_give),
    t.rate ?? '', t.spread_amount ?? '',
    viaWords(t) ?? '',
    t.merchant_name ?? '', operatorLabel(t.booked_by_admin),
    t.booked_at,
  ].map(csvEscape).join(','));
  const blob = new Blob([[header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `trades-${dubaiDate()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── screen ───────────────────────────────────────────────────────────────────

export function TradesLog() {
  const q = useQuery({
    queryKey: ['trades', 'log', 'dubaiotc', 200],
    queryFn:  () => api.listTrades('dubaiotc', 200),
  });

  // "All trades →" on a party page lands here with ?party=<slug>, and a
  // statement row with ?q=<deal id>: the search box starts on it. Read once,
  // as the initial state — the box is then the operator's.
  const [params] = useSearchParams();
  // A deal looked up by id is found whatever day it was booked (§5.14's
  // rule for history doors); the bare screen opens on today.
  const [period, setPeriod]   = useState<PeriodValue>(() => (params.get('q') ? { key: 'all' } : { key: 'today' }));
  const [kind, setKind]       = useState<KindFilter>('all');
  const [asset, setAsset]     = useState<string>('all');
  const [status, setStatus]   = useState<string>('all');
  const [search, setSearch]   = useState(() => params.get('q') ?? params.get('party') ?? '');
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selected, setSelected] = useState<TradeRow | null>(null);

  const rows = q.data?.trades ?? [];
  const days = periodDays(period);

  const assetOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of rows) { set.add(displayAsset(t.asset_take)); set.add(displayAsset(t.asset_give)); }
    return [...set].sort();
  }, [rows]);

  // The route excludes voided deals, so this can only ever offer Settled,
  // Carried in and Awaiting (plus the cheque states).
  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of rows) { const l = rowLifecycle(t); if (l) set.add(l); }
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((t) => {
      // CARRIED-IN ROWS ARE HIDDEN UNLESS ASKED FOR (§5.13): the mirror
      // replays of the pre-cutover trades sit inside the 1 Sept opening box,
      // and listing them beside the 1 Sept trades showed the same money twice.
      const lc = rowLifecycle(t);
      if (status === 'all' ? lc === 'absorbed' : lc !== status) return false;
      if (!inPeriod(t.booked_at, days)) return false;
      if (kind !== 'all' && tradeKind(t) !== kind) return false;
      if (asset !== 'all' && displayAsset(t.asset_take) !== asset && displayAsset(t.asset_give) !== asset) return false;
      if (needle) {
        // Search both the displayed name and the stored one: an operator who
        // types the legacy string they remember ("GRYPTO TG") must still find
        // the row that now reads "Grypto".
        const hay = [
          tradeParty(t), t.party_name ?? '', t.party_slug,
          tradeMerchant(t), t.merchant_name ?? '',
          t.deal_id,
        ].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, days.from, days.to, kind, asset, status, search]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'time':   return dir * (Date.parse(a.booked_at) - Date.parse(b.booked_at));
        case 'party':  return dir * tradeParty(a).localeCompare(tradeParty(b));
        case 'rate':   return dir * (Number(a.rate ?? 0) - Number(b.rate ?? 0));
        default:       return 0;
      }
    });
  }, [filtered, sortKey, sortDir]);

  // The blocks are cut from the sorted rows, so the sort applies inside every
  // block. The Asset chip has already narrowed the rows; what remains is
  // grouped, so a chip on the other leg (INR) still shows the USDT·TRC block
  // holding the INR deals.
  const blocks = useMemo(() => groupByTraded(sorted), [sorted]);
  // The one visible column header is the first block's: its Status word asks ALL the deals (a later block may hold
  // the only awaiting ones — Astra review, 21 Oct 2026).
  const anyOpen = sorted.some((t) => { const l = rowLifecycle(t); return l && l !== 'settled'; });

  // Toolbar volume line (lg only) — fiat-leg totals over the filtered rows,
  // largest two assets. Pure presentation on already-fetched data.
  const volTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const t of filtered) {
      const leg = fiatLeg(t);
      if (!leg) continue;
      const n = Number(leg.amount);
      if (!Number.isFinite(n)) continue;
      totals[leg.asset] = (totals[leg.asset] ?? 0) + n;
    }
    return Object.entries(totals).sort((a, b) => b[1] - a[1]).slice(0, 2);
  }, [filtered]);

  const onSort = (k: SortKey) => {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir(k === 'time' ? 'desc' : 'asc'); }
  };

  // "Clear filters" shows at the bar's end once anything narrows the log
  // beyond the bare screen (today, everything, all types, all assets); it
  // puts the bar back there. The sort is a view, not a filter — it stays.
  const filtersSet = search !== '' || kind !== 'all' || asset !== 'all' || status !== 'all' || period.key !== 'today';
  const clearFilters = () => {
    setSearch(''); setKind('all'); setAsset('all'); setStatus('all'); setPeriod({ key: 'today' });
  };

  const header = (
    <ScreenHeader
      className="lg:shrink-0"
      title="Trades"
      help={
        <>
          <p>
            What the desk agreed: each trade at its rate, with whom. The money that moved is on
            Money movements, a trade&rsquo;s legs included.
          </p>
          <p>
            Trades from before 1 Sept were carried into the opening box. They are hidden unless
            Status is set to Carried in, so the 1 Sept trades do not appear twice.
          </p>
          <p>
            The log holds the newest 200 trades. Tap a row for the deal panel; Export CSV writes the
            rows on screen.
          </p>
        </>
      }
    />
  );
  // THE TITLE STAYS WHEN THE READ DOES NOT COME (22 Oct 2026): a bare red sentence, with no title and no way out, was all
  // a failed read left. A first read that failed is one amber line with Try again; a REFRESH that failed keeps the rows
  // it had (TanStack puts the whole query in error) and says so above them.
  // PENDING, not "loading": opened with no connection the first read is paused — no answer and nothing fetching, so isLoading and
  // isError are both false — and it is waiting, never "Nothing in this period" (Astra, 22 Oct 2026).
  if (q.isPending) return <div className="space-y-3">{header}<Pending /></div>;
  if (q.isError && !q.data) {
    return (
      <div className="space-y-3">
        {header}
        <ReadFailed what="the trades" error={q.error} retry={() => { void q.refetch(); }} />
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-0 lg:flex lg:flex-col lg:gap-3 lg:h-[calc(100dvh-7.5rem)]">
      {header}
      {q.isError && (
        <ReadFailed what="the trades" error={q.error} says={sorted.length === 0 ? LAST_READ_NONE : 'showing the last good read'} retry={() => { void q.refetch(); }} className="lg:shrink-0" />
      )}

      {/* Table card — full width; the filters are its top line. */}
      <div className="card p-0 overflow-hidden min-w-0 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <FilterBar
          moreOpen={kind !== 'all' || asset !== 'all' || status !== 'all'}
          trailing={filtersSet && (
            <button type="button" className="btn-link text-meta" onClick={clearFilters}>Clear filters</button>
          )}
          more={
            <>
              <FilterItem label="Type">
                {KINDS.map(([k, label]) => (
                  <Chip key={k} active={kind === k} onClick={() => setKind(k)}>{label}</Chip>
                ))}
              </FilterItem>
              <FilterPair>
                <FilterItem label="Asset">
                  <FilterSelect
                    value={asset}
                    onChange={setAsset}
                    allValue="all"
                    options={assetOptions.map((a) => ({ value: a, label: a }))}
                  />
                </FilterItem>
                {statusOptions.length > 0 && (
                  <FilterItem label="Status">
                    <FilterSelect
                      value={status}
                      onChange={setStatus}
                      allValue="all"
                      options={statusOptions.map((s) => ({ value: s, label: lifecycleLabel(s) }))}
                    />
                  </FilterItem>
                )}
              </FilterPair>
            </>
          }
          toolbar={sorted.length === 0 ? undefined : (
            <>
              <p className="text-meta text-tg-hint">
                <span className="font-mono tabular text-tg-text font-medium">{sorted.length}</span>
                {' '}deals · {periodWords(period)}
                {volTotals.map(([a, n], i) => (
                  <span key={a} className="hidden lg:inline">
                    {' · '}<span className="whitespace-nowrap">{i === 0 && 'vol '}{displayAsset(a)}{' '}
                    <span className="font-mono tabular text-tg-text">{fmtAmount(Math.round(n), 'AED')}</span></span>
                  </span>
                ))}
              </p>
              <button
                type="button"
                className="btn-secondary text-meta py-1 px-2.5 flex items-center gap-1.5 lg:rounded-full lg:py-[3px]"
                disabled={sorted.length === 0}
                onClick={() => exportCsv(sorted)}
              >
                <Download size={12} /> Export CSV
              </button>
            </>
          )}
        >
          <FilterItem label="Search">
            {/* Names what the haystack holds — the party and the id. */}
            <input
              className="input w-full lg:w-56 lg:text-meta"
              placeholder="Party or deal id…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </FilterItem>
          <FilterItem label="When">
            <PeriodChips value={period} onChange={setPeriod} allTime />
          </FilterItem>
        </FilterBar>

        <div className="overflow-x-auto lg:overflow-y-auto lg:flex-1 lg:min-h-0">
          {blocks.map((b, bi) => (
            <section key={b.label} className={`${LEDGER.block} pt-3 pb-1 px-1 lg:px-1.5`}>
              <LedgerHead
                title={b.label}
                figure={
                  // LedgerHead sets the figure in .amt; the two words step
                  // back to the text face so only the numbers are mono. On
                  // the phone the two figures stack (bought over sold) rather
                  // than break mid-sentence. A half that is nothing is not
                  // said ("bought 0 · sold 0.15", "sold 0" — 21 Oct 2026 audit).
                  <span className="inline-flex flex-col items-end lg:flex-row lg:items-baseline lg:gap-1">
                    {b.bought > 0 && <span><span className="font-sans font-normal text-tg-hint">bought </span><span className="amt">{fmtAmount(b.bought, b.code)}</span></span>}
                    {b.sold > 0 && <span><span className={`font-sans font-normal text-tg-hint${b.bought > 0 ? " lg:before:content-['·_']" : ''}`}>sold </span><span className="amt">{fmtAmount(b.sold, b.code)}</span></span>}
                  </span>
                }
                note={`${b.deals.length} ${b.deals.length === 1 ? 'deal' : 'deals'}`}
              />
              {/* THE PHONE (operator, 19 Sept 2026: "fix the phone layout for
                  the trades screen too"): one card per deal on the Ledger's
                  deal row — stamp · party · @ rate, then the customer's legs
                  and the state — the same two lines Home's Recent trades use,
                  instead of a seven-column table scrolled sideways. */}
              <div className="lg:hidden divide-y divide-tg-secondary">
                {b.deals.map((t) => (
                  <DealRowLine key={t.deal_id} deal={t} onOpen={() => setSelected(t)} className="px-1" />
                ))}
              </div>
              {/* When · Type · Party · Pays · Receives · Rate · Status —
                  the Type pill second, as on the statement and Money. */}
              {/* ONE VISIBLE COLUMN HEADER for the log (21 Oct 2026 audit): the
                  blocks share their seats, and four of five blocks holding one
                  deal each repeated the whole line. Later blocks COLLAPSE theirs
                  (visibility: collapse takes the row out but leaves the column
                  widths it sets — removing the header moved every column). */}
              <table className={`${LEDGER.table} min-w-[1000px] hidden lg:table`}>
                <thead className={bi > 0 ? 'collapse' : undefined}>
                  <tr>
                    <Th label="When"   k="time"   sortKey={sortKey} sortDir={sortDir} onSort={onSort} className={LEDGER.whenStampW} />
                    <th className={`${LEDGER.th} ${LEDGER.kind}`}>Type</th>
                    <Th label="Party"  k="party"  sortKey={sortKey} sortDir={sortDir} onSort={onSort} className={LEDGER.words} />
                    {/* The customer's two legs, Book trade's words (§6). */}
                    <th className={`${LEDGER.th} ${LEDGER.figWide} text-right`}>Pays</th>
                    <th className={`${LEDGER.th} ${LEDGER.figWide} text-right`}>Receives</th>
                    <Th label="Rate"   k="rate"   sortKey={sortKey} sortDir={sortDir} onSort={onSort} className={`${LEDGER.fig} text-right`} />
                    {/* The word only over a column with something in it (Home's rule). */}
                    <th className={`${LEDGER.th} ${LEDGER.status}`}>{anyOpen ? 'Status' : ''}</th>
                    <th className={LEDGER.slack} aria-hidden="true" />
                  </tr>
                </thead>
                <tbody>
                  {b.deals.map((t) => (
                    <TradeRowItem
                      key={t.deal_id}
                      trade={t}
                      selected={selected?.deal_id === t.deal_id}
                      onClick={() => setSelected(t)}
                    />
                  ))}
                </tbody>
              </table>
            </section>
          ))}
          {/* AN EMPTY CLAIM NEEDS A SUCCESSFUL READ: not one that failed (the line above says what the LAST read showed) and
              not one that has not come (it waits, above). */}
          {sorted.length === 0 && q.isSuccess && (
            <EmptyState
              className="px-3 lg:py-16"
              action={filtersSet ? <button type="button" className="btn-link" onClick={clearFilters}>Clear filters</button> : undefined}
            >
              {period.key === 'all' ? 'Nothing here.' : 'Nothing in this period.'}
            </EmptyState>
          )}
        </div>
      </div>

      <DealSlideOver deal={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

// ── pieces ───────────────────────────────────────────────────────────────────

// A column line is LEDGER's plain header, not the sticky one: the log is
// several currency blocks, and a line that stayed put once its block's own
// header had scrolled away would name columns for a currency the eye could
// not see.
function Th({ label, k, sortKey, sortDir, onSort, className = '' }: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sortKey === k;
  return (
    <th
      onClick={() => onSort(k)}
      className={`${LEDGER.th} ${className} cursor-pointer select-none hover:text-tg-text ${active ? 'text-tg-accent' : ''}`}
    >
      {label}{active && (sortDir === 'desc' ? ' ▾' : ' ▴')}
    </th>
  );
}

/** One leg, one figure cell: "69,293.50 USDT" (§5.13), the primitive's one
 *  rendering of an amount with its code. */
/** One leg, one cell. A cheque's leg carries its word before the figure —
 *  "deposit" / "cheque" — on one line while they fit (the wide figure seat, since
 *  22 Oct 2026), the figure under its word when they do not. */
function Leg({ amount, asset, word }: { amount: string | number; asset: string; word?: string }) {
  // The word and the figure share a line while they fit; a 9-digit cheque face puts the figure on the line under its word
  // instead of printing over the next column (22 Oct 2026, the stress render).
  return (
    <td className={`${LEDGER.td} ${LEDGER.figWide}`}>
      <div className="flex flex-wrap items-baseline justify-end gap-x-1.5">
        {word && <span className="text-meta text-tg-hint">{word}</span>}
        <Amount value={amount} asset={asset} />
      </div>
    </td>
  );
}

function TradeRowItem({ trade, selected, onClick }: { trade: TradeRow; selected: boolean; onClick: () => void }) {
  const kind = tradeKind(trade);
  const lifecycle = rowLifecycle(trade);
  const party = tradeParty(trade);
  return (
    <tr
      onClick={onClick}
      title={party}
      className={`cursor-pointer border-b border-tg-secondary lg:border-stroke-soft last:border-b-0 transition-colors hover:bg-tg-secondary ${selected ? 'bg-tg-secondary lg:bg-tg-accent/[0.06]' : ''}`}
    >
      <td className={`${LEDGER.td} ${LEDGER.whenStamp}`}>{fmtStamp(trade.booked_at)}</td>
      {/* The kind word through the shared map — Buy and Sell wear the Trade
          tone, one colour for both (no colour by direction, item 72). */}
      <td className={`${LEDGER.td} ${LEDGER.kind}`}><KindPill label={KIND_LABEL[kind]} /></td>
      {/* The one column that gives way. */}
      <td className={`${LEDGER.td} ${LEDGER.words}`}><div className="who truncate">{party}</div></td>
      {/* The customer pays the take leg and receives the give leg — the
          CSV's columns, the deal panel's rows, Book trade's sentence. */}
      {/* A cheque is not a trade at rate 1 (operator, 19 Sept 2026): the
          take leg is the deposit the customer paid, the give leg the cheque's
          face, and the rate cell stays empty. */}
      <Leg amount={trade.amount_take} asset={trade.asset_take} word={kind === 'mc' ? 'deposit' : undefined} />
      <Leg amount={trade.amount_give} asset={trade.asset_give} word={kind === 'mc' ? 'cheque' : undefined} />
      <td className={`${LEDGER.td} ${LEDGER.fig}`}>{kind !== 'mc' && <span className="amt">{fmtRate(trade.rate, rateQuote(trade))}</span>}</td>
      {/* Settled is the norm, so it says nothing; only a row that is not
          settled carries a pill (§5.13). */}
      <td className={`${LEDGER.td} ${LEDGER.status}`}>
        {lifecycle && lifecycle !== 'settled' && (
          <Pill tone={lifecycleTone(lifecycle)}>{lifecycleLabel(lifecycle)}</Pill>
        )}
      </td>
      <td className={LEDGER.slack} aria-hidden="true" />
    </tr>
  );
}
